import { PageBanner, RiIcon, Section, pick as pickBi, siteUrlFor } from '@longnan/ui';
import { assetUrl, useT } from '../i18n';
import { creditOf, findImage } from '../useSiteData';
import {WithSiteData} from './common';
import type { SiteData } from '../useSiteData';

/**
 * 板块配图：按板块键映射到本站图片清单中的陇南实拍图。
 * 图片 id 保持稳定，页面只在此引用字面量，`npm run check:refs` 会校验它们存在。
 */
function segmentImage(d: SiteData, key: string) {
  const id = `segment-${key}`;
  return d.imagery.items.some((i) => i.id === id) ? findImage(d.imagery, id) : findImage(d.imagery, 'convention');
}

function Banner({ d }: { d: SiteData }) {
  const { t, pick } = useT();
  const img = findImage(d.imagery, 'skyline');
  return (
    <PageBanner
      image={assetUrl(img.local)}
      imageAlt={pick(img.alt)}
      imageCredit={creditOf(img)}
      eyebrow={t('eco.eyebrow')}
      title={t('eco.title')}
      lead={t('eco.lead')}
    />
  );
}

function Rows({ d }: { d: SiteData }) {
  const { t, pick, locale } = useT();
  return (
    <Section id="rows">
      <ul className="space-y-6">
        {d.eco.subsidiaries.map((s, i) => {
          const img = segmentImage(d, s.key);
          const credit = creditOf(img);
          return (
          <li key={s.key} className="grid gap-8 rounded-2xl border border-line bg-bg p-6 md:grid-cols-[1fr_1.4fr] md:p-8">
            <figure className={i % 2 === 1 ? 'md:order-2' : ''}>
              <img
                src={assetUrl(img.local)}
                alt={pick(img.alt)}
                loading="lazy"
                className="aspect-[4/3] w-full rounded-xl border border-line object-cover"
              />
              {credit && <figcaption className="mt-2 text-[11px] text-muted">{credit}</figcaption>}
            </figure>
            <div className="flex flex-col">
              <div className="flex flex-wrap items-center gap-3">
                <span aria-hidden="true" className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-on-dark">
                  <RiIcon name={s.mark} className="text-xl" />
                </span>
                <h2 className="text-2xl font-semibold text-body">{pick(s.shortName)}</h2>
              </div>
              <p className="mt-1 text-xs text-muted">{pick(s.legalName)}</p>
              <p className="mt-5 text-lg leading-relaxed text-body">{pick(s.positioning)}</p>
              <div className="mt-6">
                <div className="text-xs font-semibold tracking-widest text-muted">{t('eco.keywords')}</div>
                <ul className="mt-3 flex flex-wrap gap-2">
                  {s.keywords.map((k, j) => (
                    <li key={j} className="rounded-full border border-line bg-surface px-3 py-1 text-sm text-muted-light">
                      {pickBi(k, locale)}
                    </li>
                  ))}
                </ul>
              </div>
              <a
                href={siteUrlFor(s.key, locale)}
                className="mt-auto inline-flex w-fit items-center rounded-md border border-primary px-5 py-2.5 text-sm font-semibold text-primary-light hover:bg-primary hover:text-on-dark"
              >
                {t('common.visit')} {pick(s.shortName)} →
              </a>
            </div>
          </li>
          );
        })}
      </ul>
    </Section>
  );
}

export function Ecosystem() {
  return (
    <WithSiteData>
      {(d) => (
        <>
          <Banner d={d} />
          <Rows d={d} />
        </>
      )}
    </WithSiteData>
  );
}
